/**
 * Application constants.
 */

// ── Storage Keys ──
export const STORAGE_KEYS = {
  GATEWAYS: 'voice-ai-gateways',
  ACTIVE_GATEWAY: 'voice-ai-active-gateway',
  PLAYBACK_SPEED: 'voice-ai-speed',
  MIC_MUTED: 'voice-ai-mic-muted',
  SPEAKER_MUTED: 'voice-ai-speaker-muted',
  VOICE_INTERRUPT: 'voice-ai-voice-interrupt',
  VAD_THRESHOLD: 'voice-ai-vad-threshold',
  VAD_ADAPTIVE: 'voice-ai-vad-adaptive',
  SPOKEN_MESSAGES: 'voice-ai-spoken',
  SELECTED_MODEL: 'voice-ai-model',
  OPENAI_API_KEY: 'openai-api-key',
  STT_ENGINE: 'voice-ai-stt-engine',
  TTS_PROVIDER: 'voice-ai-tts-provider',
  EDGE_TTS_BACKEND_URL: 'voice-ai-edge-tts-url',
  /** @deprecated Legacy token key — prefer JWT via getApiAuthHeaders() */
  EDGE_TTS_AUTH_TOKEN: 'voice-ai-edge-tts-token',
  UPLOAD_BACKEND_URL: 'voice-ai-upload-backend-url',
  UPLOAD_AUTH_TOKEN: 'voice-ai-upload-auth-token',
  DEBUG_LOGGING: 'voice-ai-debug-logging',
  TTS_DEBUG: 'voice-ai-tts-debug',
  SHOW_THINKING_STREAM: 'showThinkingStream',
  AUTO_REASONING_STREAM_SESSIONS: 'talkyn-auto-reasoning-stream-sessions',
  SUBAGENT_BUBBLE_MODE: 'subagent-bubble-mode',
  SUBAGENT_DEPTH: 'subagent-depth',
  SESSION_QUICK_SWITCH: 'talkyn-session-quick-switch',
  SESSION_QUICK_SWITCH_SIDE: 'talkyn-session-quick-switch-side',
  SESSION_SWITCH_READOUT: 'talkyn-session-switch-readout',
  SEND_WHILE_RUNNING_MODE: 'talkyn-send-while-running-mode',
  CUSTOM_BACKEND_URL: 'talkyn-backend-url',
  CF_UPLOADS_DOMAIN: 'talkyn-cf-uploads-domain',
  ENV_OVERRIDE: 'talkyn-env-override',
  PREVIEW_PR_NUMBER: 'talkyn-preview-pr-number',
  PREVIEW_PR_DRAFT: 'talkyn-preview-pr-draft',
  VOICE_LOCK_ENABLED: 'voice-lock-enabled',
  VOICE_LOCK_PROFILE: 'voice-lock-profile',
  VOICE_LOCK_TRAINED_AT: 'voice-lock-trained-at',
  VOICE_LOCK_MODEL: 'voice-lock-model',
  VOICE_LOCK_THRESHOLD: 'voice-lock-threshold',
  VOICE_LOCK_GATE_ENABLED: 'voice-lock-gate-enabled',
  VOICE_LOCK_EMBEDDINGS: 'voice-lock-embeddings',
  PREFERRED_LANGUAGES: 'talkyn-preferred-languages',
  ALSO_REPLY_IN: 'talkyn-also-reply-in',
  VOICE_GENDER: 'talkyn-voice-gender',
  VOICE_OVERRIDES: 'talkyn-voice-overrides',
} as const;

// ── Protocol Constants ──
export const PROTOCOL_VERSION = 3;
export const DEFAULT_SESSION_KEY = 'agent:main:main';

/**
 * Read the `?agent=` URL parameter once and build the initial session key.
 * If present, returns `agent:<agentId>:main`; otherwise falls back to DEFAULT_SESSION_KEY.
 * The agent value is sanitized to allow only alphanumeric characters, hyphens, and underscores.
 */
export function getInitialSessionKey(): string {
  if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    const agent = params.get('agent');
    if (agent && /^[a-zA-Z0-9_-]+$/.test(agent)) {
      return `agent:${agent}:main`;
    }
  }
  return DEFAULT_SESSION_KEY;
}

/** Client identity sent in connect request */
export const CLIENT_INFO = {
  id: 'webchat-ui',
  version: '1.0.6',
  platform: 'web',
  mode: 'webchat',
} as const;

export const CLIENT_ROLE = 'operator';
export const CLIENT_SCOPES = ['operator.read', 'operator.write', 'operator.admin'] as const;

// ── Reconnection ──
export const RECONNECT_BASE_DELAY_MS = 1000;
export const RECONNECT_MAX_DELAY_MS = 30000;

// ── Request Timeouts ──
export const DEFAULT_REQUEST_TIMEOUT_MS = 30000;

// ── Keepalive ──
export const DEFAULT_TICK_INTERVAL_MS = 30000;

// ── Pairing ──
export const PAIRING_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes
export const PAIRING_RETRY_INTERVAL_MS = 10000;

// ── VAD Defaults ──
export const VAD_DEFAULT_POSITIVE_THRESHOLD = 0.8;
export const VAD_DEFAULT_NEGATIVE_THRESHOLD = 0.3;

// ── UI ──
export const SPEED_STEPS = [1, 1.2, 1.5, 2] as const;
export const SPOKEN_MAX_IDS = 200;

// ── URL Gateway Params ──

interface UrlGatewayConfig {
  id: string;
  name: string;
  url: string;
  sessionKey: string;
  device: null;
  deviceToken: null;
  gatewayToken: string;
  status: 'disconnected';
  lastConnected: null;
}

/**
 * Read `gwUrl` and `gwToken` from URL query parameters. If both are present,
 * upsert a gateway entry in localStorage and set it as the active gateway so
 * the app auto-connects on load.
 *
 * Example URL:
 *   http://localhost:5199/app/?agent=ci-verify&gwUrl=ws://localhost:18789&gwToken=TOKEN
 */
export function applyUrlGatewayParams(): void {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  const gwUrl = params.get('gwUrl');
  const gwToken = params.get('gwToken');

  if (!gwUrl || !gwToken) return;

  // Build session key using the same logic as getInitialSessionKey()
  const sessionKey = getInitialSessionKey();

  // Parse hostname from gwUrl for display name
  let name = gwUrl;
  try {
    const parsed = new URL(gwUrl);
    name = parsed.hostname || gwUrl;
  } catch {
    // If URL parsing fails, use the raw gwUrl as the name
  }

  // Load existing gateways
  let gateways: UrlGatewayConfig[] = [];
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.GATEWAYS);
    if (raw) {
      gateways = JSON.parse(raw);
    }
  } catch {
    gateways = [];
  }

  // Find existing gateway by url
  const existingIndex = gateways.findIndex((gw) => gw.url === gwUrl);
  let gatewayId: string;

  if (existingIndex >= 0) {
    // Update existing
    gatewayId = gateways[existingIndex].id;
    gateways[existingIndex] = {
      ...gateways[existingIndex],
      gatewayToken: gwToken,
      sessionKey,
      name,
    };
  } else {
    // Create new
    gatewayId = crypto.randomUUID();
    gateways.push({
      id: gatewayId,
      name,
      url: gwUrl,
      sessionKey,
      device: null,
      deviceToken: null,
      gatewayToken: gwToken,
      status: 'disconnected',
      lastConnected: null,
    });
  }

  localStorage.setItem(STORAGE_KEYS.GATEWAYS, JSON.stringify(gateways));
  localStorage.setItem(STORAGE_KEYS.ACTIVE_GATEWAY, gatewayId);
}

// Run once at module load, before React mounts
applyUrlGatewayParams();
